import { useEffect, useRef, useState, useCallback } from 'react';
import { Board, Pixel } from '@/types';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/atoms/ui/card';
import { formatTimestamp } from '@/lib/utils';
import { FiLock } from 'react-icons/fi';
import { getBoardPixels } from '@/services/board.service';
import { isFirebaseError, FIREBASE_ERROR_CODES } from '@/types/errors';

interface BoardCardComponentProps {
  board: Board;
  onCardClick: () => void;
  onOwnerClick: () => void;
}

const DEFAULT_BG_COLOR = '#101322'; // Default background color for empty pixels

export const BoardCardComponent: React.FC<BoardCardComponentProps> = ({
  board,
  onCardClick,
  onOwnerClick,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasPreview, setHasPreview] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  const renderPreview = useCallback((pixels: (Pixel | null)[][]) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Set canvas to actual board dimensions
    canvas.width = board.width;
    canvas.height = board.height;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Disable image smoothing for crisp pixel art
    ctx.imageSmoothingEnabled = false;

    // Fill background
    ctx.fillStyle = DEFAULT_BG_COLOR;
    ctx.fillRect(0, 0, board.width, board.height);

    // Draw pixels at 1:1 scale (each pixel is 1 canvas pixel)
    for (let y = 0; y < board.height; y++) {
      for (let x = 0; x < board.width; x++) {
        const pixel = pixels[y]?.[x];
        if (pixel) {
          ctx.fillStyle = pixel.color;
          ctx.fillRect(x, y, 1, 1);
        }
      }
    }
  }, [board.width, board.height]);

  const loadPreview = useCallback(async () => {
    if (isLoading || hasPreview || error) return;

    setIsLoading(true);
    setError(null);
    try {
      const pixels = await getBoardPixels(board.id);
      renderPreview(pixels);
      setHasPreview(true);
    } catch (err) {
      console.error('Error loading board preview:', err);
      
      // Check if it's a permission error - don't retry these
      if (isFirebaseError(err) && err.code === FIREBASE_ERROR_CODES.PERMISSION_DENIED) {
        setError('Access denied');
        setIsLoading(false);
        return;
      }
      
      // For other errors, allow retry (but don't set hasPreview)
      setError('Failed to load preview');
    } finally {
      setIsLoading(false);
    }
  }, [board.id, isLoading, hasPreview, error, renderPreview]);

  // Lazy load preview when card is visible
  useEffect(() => {
    if (!cardRef.current) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasPreview && !isLoading && !error) {
            loadPreview();
            observerRef.current?.disconnect();
          }
        });
      },
      { rootMargin: '50px' }
    );

    observerRef.current.observe(cardRef.current);

    return () => {
      observerRef.current?.disconnect();
    };
  }, [board.id, hasPreview, isLoading, error, loadPreview]);

  return (
    <Card
      ref={cardRef}
      className="board-card-component cursor-pointer transition-all overflow-hidden"
      onClick={onCardClick}
    >
      {/* Preview Canvas */}
      <div className="w-full bg-muted/50 flex items-center justify-center aspect-video relative">
        <canvas
          ref={canvasRef}
          className="w-full h-full object-cover"
          style={{
            imageRendering: 'pixelated',
            display: hasPreview ? 'block' : 'none',
          }}
        />
        {!hasPreview && (
          <div className="absolute inset-0 flex items-center justify-center text-muted-foreground text-sm">
            {error ? (
              <span className="text-destructive text-xs">{error}</span>
            ) : isLoading ? (
              'Loading...'
            ) : (
              `${board.width} × ${board.height}`
            )}
          </div>
        )}
      </div>

      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>{board.name}</span>
          <div className="flex items-center gap-2">
            {!board.isPublic && (
              <span className="text-xs bg-muted text-muted-foreground px-2 py-1 rounded flex items-center gap-1">
                <FiLock className="h-3 w-3" />
                Private
              </span>
            )}
            {board.isSpecialEvent && (
              <span className="text-xs bg-primary text-primary-foreground px-2 py-1 rounded">
                Event
              </span>
            )}
          </div>
        </CardTitle>
        <CardDescription>
          by{' '}
          <span
            className="hover:underline cursor-pointer font-medium"
            onClick={(e) => {
              e.stopPropagation();
              onOwnerClick();
            }}
          >
            {board.ownerUsername}
          </span>
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-2 text-sm text-muted-foreground">
          <p>
            Dimensions: {board.width} × {board.height} (
            {board.maxPixels.toLocaleString()} pixels)
          </p>
          <p>Created {formatTimestamp(board.createdAt.toDate())}</p>
        </div>
      </CardContent>
    </Card>
  );
};

